const width = {
    name: "width",
    path: "/width",
    title: "width",
    description: "Utilities for controlling the width of elements.",
    value: {
        "width": {
            "w-<number>": "calc(var(--1) * <number>)",
            "w-<fraction>": "calc(<fraction> * 100%)",
            "w-3xs": "var(--container-3xs); /* 16rem (256px) */",
            "w-2xs": "var(--container-2xs); /* 18rem (288px) */",
            "w-xs": "var(--container-xs); /* 20rem (320px) */",
            "w-sm": "var(--container-sm); /* 24rem (384px) */",
            "w-md": "var(--container-md); /* 28rem (448px) */",
            "w-lg": "var(--container-lg); /* 32rem (512px) */",
            "w-xl": "var(--container-xl); /* 36rem (576px) */",
            "w-2xl": "var(--container-2xl); /* 42rem (672px) */",
            "w-3xl": "var(--container-3xl); /* 48rem (768px) */",
            "w-4xl": "var(--container-4xl); /* 56rem (896px) */",
            "w-5xl": "var(--container-5xl); /* 64rem (1024px) */",
            "w-6xl": "var(--container-6xl); /* 72rem (1152px) */",
            "w-7xl": "var(--container-7xl); /* 80rem (1280px) */",
            "w-auto": "auto",
            "w-px": "1px",
            "w-full": "100%",
            "w-screen": "100vw",
            "w-dvw": "100dvw",
            "w-dvh": "100dvh",
            "w-lvw": "100lvw",
            "w-lvh": "100lvh",
            "w-svw": "100svw",
            "w-svh": "100svh",
            "w-min": "min-content",
            "w-max": "max-content",
            "w-fit": "fit-content",
            "w-[<value>]": "<value>",
        },
        "":{
            "size-auto": "width: auto; height: auto",
            "size-px": "width: 1px; height: 1px",
            "size-full": "width: 100%; height: 100%",
            "size-dvw": "width: 100dvw; height: 100dvw",
            "size-dvh": "width: 100dvh; height: 100dvh",
            "size-lvw": "width: 100lvw; height: 100lvw",
            "size-lvh": "width: 100lvh; height: 100lvh",
            "size-svw": "width: 100svw; height: 100svw",
            "size-svh": "width: 100svh; height: 100svh",
            "size-min": "width: min-content; height: min-content",
            "size-max": "width: max-content; height: max-content",
            "size-fit": "width: fit-content; height: fit-content",
            "size-[<value>]": "width: <value>; height: <value>",
            "size-<fraction>": "width: calc(<fraction> * 100%); height: calc(<fraction> * 100%)",
        }
    },
    exampleData: [
        {
            "example": {
                "code": `
<div class="w-96 bg-color-blue-500  rounded-lg p-2 text-center ">w-96</div>
<div class="w-80 bg-color-blue-500 rounded-lg p-2 text-center ">w-80</div>
<div class="w-64 bg-color-blue-500 rounded-lg p-2 text-center ">w-64</div>
<div class="w-48 bg-color-blue-500 rounded-lg p-2 text-center ">w-48</div>
<div class="w-40 bg-color-blue-500 rounded-lg p-2 text-center ">w-40</div>
<div class="w-32 bg-color-blue-500 rounded-lg p-2 text-center ">w-32</div>
<div class="w-24 bg-color-blue-500 rounded-lg p-2 text-center ">w-24</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="flex ...">
  <div class="w-1/2 ...">w-1/2</div>
  <div class="w-1/2 ...">w-1/2</div>
</div>
<div class="flex ...">
  <div class="w-2/5 ...">w-2/5</div>
  <div class="w-3/5 ...">w-3/5</div>
</div>
<div class="flex ...">
  <div class="w-1/3 ...">w-1/3</div>
  <div class="w-2/3 ...">w-2/3</div>
</div>
<div class="flex ...">
  <div class="w-1/4 ...">w-1/4</div>
  <div class="w-3/4 ...">w-3/4</div>
</div>
<div class="flex ...">
  <div class="w-1/5 ...">w-1/5</div>
  <div class="w-4/5 ...">w-4/5</div>
</div>
<div class="flex ...">
  <div class="w-1/6 ...">w-1/6</div>
  <div class="w-5/6 ...">w-5/6</div>
</div>
<div class="w-full ...">w-full</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="w-xl ...">w-xl</div>
<div class="w-lg ...">w-lg</div>
<div class="w-md ...">w-md</div>
<div class="w-sm ...">w-sm</div>
<div class="w-xs ...">w-xs</div>
<div class="w-2xs ...">w-2xs</div>
<div class="w-3xs ...">w-3xs</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="size-[64px] ...">size-16</div>
<div class="size-20 ...">size-20</div>
<div class="size-24 ...">size-24</div>
<div class="size-32 ...">size-32</div>
<div class="size-40 ...">size-40</div>
                `
            }
        }
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">w-&lt;number&gt;</code> utilities to set fixed width values:
            </p>
            <div class="space-y-4 text-color-white font-mono font-bold">
                <div class="w-96 bg-color-blue-500  rounded-lg p-2 text-center ">w-96</div>
                <div class="w-80 bg-color-blue-500 rounded-lg p-2 text-center ">w-80</div>
                <div class="w-64 bg-color-blue-500 rounded-lg p-2 text-center ">w-64</div>
                <div class="w-48 bg-color-blue-500 rounded-lg p-2 text-center ">w-48</div>
                <div class="w-40 bg-color-blue-500 rounded-lg p-2 text-center ">w-40</div>
                <div class="w-32 bg-color-blue-500 rounded-lg p-2 text-center ">w-32</div>
                <div class="w-24 bg-color-blue-500 rounded-lg p-2 text-center ">w-24</div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">w-full, w-&lt;fraction&gt;</code>
                utilities like w-1/2 and w-2/5 to give an element a percentage-based width:
            </p>
            <div class="space-y-4">
                <div class="flex gap-4 text-color-white font-mono font-bold">
                    <div class="w-1/2 bg-color-blue-500  rounded-lg p-2 text-center ">w-1/2</div>
                    <div class="w-1/2 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/2</div>
                </div>
                <div class="flex gap-4 text-color-white font-mono font-bold">
                    <div class="w-2/5 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/3</div>
                    <div class="w-3/5 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/4</div>
                </div>
                <div class="flex gap-4 text-color-white font-mono font-bold">
                    <div class="w-1/3 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/5</div>
                    <div class="w-2/3 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/6</div>
                </div>
                <div class="flex gap-4 text-color-white font-mono font-bold">
                    <div class="w-1/4 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/5</div>
                    <div class="w-3/4 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/6</div>
                </div>
                <div class="flex gap-4 text-color-white font-mono font-bold">
                    <div class="w-1/5 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/5</div>
                    <div class="w-4/5 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/6</div>
                </div>
                <div class="flex gap-4 text-color-white font-mono font-bold">
                    <div class="w-1/6 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/5</div>
                    <div class="w-5/6 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/6</div>
                </div>
                <div class="flex gap-4 text-color-white font-mono font-bold">
                    <div class="w-full bg-color-blue-500 rounded-lg p-2 text-center ">w-full</div>
                </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                <code class="text-color-gray-800 dark:text-color-white font-semibold">Using the container scale</code>
                Use utilities like w-sm and w-xl to set an element to a fixed width based on the container scale:
            </p>
            <div class="space-y-4 text-color-white font-mono font-bold">
                <div class="w-xl bg-color-blue-500  rounded-lg p-2 text-center ">w-xl</div>
                <div class="w-lg bg-color-blue-500 rounded-lg p-2 text-center ">w-lg</div>
                <div class="w-md bg-color-blue-500 rounded-lg p-2 text-center ">w-md</div>
                <div class="w-sm bg-color-blue-500 rounded-lg p-2 text-center ">w-sm</div>
                <div class="w-xs bg-color-blue-500 rounded-lg p-2 text-center ">w-xs</div>
                <div class="w-2xs bg-color-blue-500 rounded-lg p-2 text-center ">w-2xs</div>
                <div class="w-3xs bg-color-blue-500 rounded-lg p-2 text-center ">w-3xs</div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                <code class="text-color-gray-800 dark:text-color-white font-semibold">Setting both width and height</code>
                Use utilities like size-px, size-4, and size-full to set both the width and height of an element at the same time:
            </p>
            <div class="flex gap-4 text-color-white font-mono font-bold">
                <div class="size-[64px] grid place-content-center place-items-center  bg-color-blue-500  rounded-lg p-2 text-center ">size-[64px]</div>
                <div class="size-20 grid place-content-center bg-color-blue-500 rounded-lg p-2 text-center ">size-20</div>
                <div class="size-24 grid place-content-center bg-color-blue-500 rounded-lg p-2 text-center ">size-24</div>
                <div class="size-32 grid place-content-center bg-color-blue-500 rounded-lg p-2 text-center ">size-32</div>
                <div class="size-40 grid place-content-center bg-color-blue-500 rounded-lg p-2 text-center ">size-40</div>
            </div>
            `
        },
    ]
};
export default width;

<div className="sr-only space-y-4 w-96 w-80 w-48 w-40 wf-1/2">
<div class="flex size-[64px] gap-4 text-color-white font-mono font-bold">
                <div class="w-1/2 bg-color-blue-500  rounded-lg p-2 text-center ">w-1/2</div>
                <div class="w-1/2 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/2</div>
            </div>
            <div class="flex gap-4 text-color-white font-mono font-bold">
                <div class="w-2/5 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/3</div>
                <div class="w-3/5 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/4</div>
            </div>
            <div class="flex gap-4 text-color-white font-mono font-bold">
                <div class="w-1/3 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/5</div>
                <div class="w-2/3 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/6</div>
            </div>
            <div class="flex gap-4 text-color-white font-mono font-bold">
                <div class="w-1/4 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/5</div>
                <div class="w-3/4 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/6</div>
            </div>
            <div class="flex gap-4 text-color-white font-mono font-bold">
                <div class="w-1/5 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/5</div>
                <div class="w-4/5 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/6</div>
            </div>
            <div class="flex gap-4 text-color-white font-mono font-bold">
                <div class="w-1/6 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/5</div>
                <div class="w-5/6 bg-color-blue-500 rounded-lg p-2 text-center ">w-1/6</div>
            </div>
            <div class="size-16 ...">size-16</div>
<div class="size-20 ...">size-20</div>
<div class="size-24 ...">size-24</div>
<div class="size-32 ...">size-32</div>
<div class="size-40 ...">size-40</div>
                            <div class="w-xl bg-color-blue-500  rounded-lg p-2 text-center ">w-xl</div>
                <div class="w-lg bg-color-blue-500 rounded-lg p-2 text-center ">w-lg</div>
                <div class="w-md bg-color-blue-500 rounded-lg p-2 text-center ">w-md</div>
                <div class="w-sm bg-color-blue-500 rounded-lg p-2 text-center ">w-sm</div>
                <div class="w-xs bg-color-blue-500 rounded-lg p-2 text-center ">w-xs</div>
                <div class="w-2xs bg-color-blue-500 rounded-lg p-2 text-center ">w-2xs</div>
                <div class="w-3xs bg-color-blue-500 rounded-lg p-2 text-center ">w-3xs</div>
</div>